import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import './ProductView.css';


const BASE = 'https://agro.atrmart.in/api';
const IMAGE_BASE = 'https://atrmart.in/storage'; // Match the Dashboard logic
const imgUrl = (p) => p ? `${IMAGE_BASE}/${p}` : 'https://via.placeholder.com/300x200?text=No+Image';

// --- Sub-Components ---
const StarRating = ({ value, onChange, readonly = false }) => (
  <div className="star-row">
    {[1, 2, 3, 4, 5].map(i => (
      <span
        key={i}
        className={`star ${i <= Math.round(value) ? 'filled' : ''} ${readonly ? '' : 'interactive'}`}
        onClick={() => !readonly && onChange && onChange(i)}
      >★</span>
    ))}
  </div>
);

const ReviewCard = ({ review }) => (
  <div className="review-card">
    <div className="review-top">
      <div className="reviewer-avatar">{review.user_name?.[0]?.toUpperCase() || 'U'}</div>
      <div className="reviewer-info">
        <span className="reviewer-name">{review.user_name || 'Customer'}</span>
        <StarRating value={review.rating} readonly />
        <span className="review-date">{new Date(review.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
      </div>
      {review.is_verified && <span className="verified-badge">✓ Verified Purchase</span>}
    </div>
    {review.title && <h4 className="review-title">{review.title}</h4>}
    <p className="review-text">{review.review_text}</p>
    {review.media && review.media.length > 0 && (
      <div className="review-media-row">
        {review.media.map((m, i) => (
          m.type === 'video'
            ? <video key={i} src={imgUrl(m.path)} controls className="review-media-thumb" />
            : <img key={i} src={imgUrl(m.path)} alt="review" className="review-media-thumb" />
        ))}
      </div>
    )}
  </div>
);

// --- Main Page ---
const ProductViewPage = ({ cart, addToCart, wishlist, toggleWishlist, updateQuantity, user, onLoginClick }) => {
    const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Core States
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [activeImage, setActiveImage] = useState('');
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);

  // UI States
  const [pincode, setPincode] = useState('');
  const [deliveryMsg, setDeliveryMsg] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [showWeightModal, setShowWeightModal] = useState(false);

  // Review states
  const [reviews, setReviews] = useState([]);
  const [avgRating, setAvgRating] = useState(0);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewName, setReviewName] = useState('');
  const [reviewTitle, setReviewTitle] = useState('');
  const [reviewText, setReviewText] = useState('');
  const [reviewMedia, setReviewMedia] = useState([]);
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewSuccess, setReviewSuccess] = useState('');

  const fileInputRef = useRef(null);

 useEffect(() => {
    if (!id) return;
    setLoading(true);
    window.scrollTo(0, 0);

axios.get(`${BASE}/products/${id}`)
      .then(res => {
        // FIX: Handle both {data: {...}} and direct {...} responses
        const item = res.data?.data || res.data;
        
        if (!item || (Array.isArray(item) && item.length === 0)) { 
            setProduct(null); 
            return; 
        }

        setProduct(item);
        setActiveImage(item.image);

        // FIX: Ensure variants are parsed correctly
        let vars = [];
        try {
          vars = typeof item.variants === 'string' ? JSON.parse(item.variants) : item.variants;
        } catch (e) { vars = []; }

        // Fallback if no variants exist
        if (!vars || vars.length === 0) {
          vars = [{ 
            unit: item.unit || item.size || '1 unit', 
            price: item.price, 
            mrp: item.mrp || item.price,
            stock_quantity: item.stock_quantity || 0 
          }];
        }
        
        const normalizedVars = vars.map(v => ({ ...v, unit: v.unit || v.size }));
        
        // Match weight from navigation state (from Dashboard)
        const preferredWeight = location.state?.selectedWeight;
        const matchedVariant = normalizedVars.find(v => v.unit === preferredWeight);

        setSelectedVariant(matchedVariant || normalizedVars[0]);

        // Fetch Related Products
        return axios.get(`${BASE}/products?category=${encodeURIComponent(item.category)}`);
      })
      .then(res => {
        if (res) {
          const all = res.data?.data || res.data || [];
          // Use Number(id) to ensure we don't compare string to number
          setRelatedProducts(all.filter(p => Number(p.id) !== Number(id)).slice(0, 6));
        }
      })
      .catch(err => {
        console.error('Fetch error:', err);
        setProduct(null);
      })
      .finally(() => setLoading(false));

    // Fetch Reviews
    axios.get(`${BASE}/products/${id}/reviews`)
      .then(res => {
        const rv = res.data?.data || res.data || [];
        setReviews(rv);
        if (rv.length > 0) {
            setAvgRating(rv.reduce((s, r) => s + Number(r.rating), 0) / rv.length);
        }
      })
      .catch(() => setReviews([]));
}, [id, location.state]);

  useEffect(() => {
    if (product && selectedVariant) {
      const cartItem = cart?.find(c => 
        c.id === product.id && 
        c.selectedWeight === (selectedVariant.unit || selectedVariant.size)
      );
      setQuantity(cartItem ? cartItem.quantity : 1);
    }
  }, [selectedVariant, cart, product]);

  if (loading) return <div className="pdv-loading"><div className="pdv-loading-spinner" /><p>Loading product…</p></div>;
  if (!product || !selectedVariant) return <div className="pdv-loading"><p>Product not found. <button onClick={() => navigate('/agro')}>← Back</button></p></div>;

  // --- DERIVED DATA (This ensures variantArray is defined for the whole return block) ---
  const images = [product.image, product['image 2'], product['image 3']].filter(Boolean);
  const maxStock = Number(selectedVariant.stock_quantity) || 0;
  const discount = selectedVariant.mrp > selectedVariant.price ? Math.round(((selectedVariant.mrp - selectedVariant.price) / selectedVariant.mrp) * 100) : 0;
  const isWishlisted = wishlist?.some(w => w.id === product.id);

  // Define variantArray here so both the main UI and the Modal can use it safely
  let variantArray = [];
  try {
    variantArray = typeof product.variants === 'string' ? JSON.parse(product.variants) : (product.variants || []);
  } catch (e) { variantArray = []; }

  if (variantArray.length === 0) {
    variantArray = [{ unit: product.unit || '1 unit', price: product.price, mrp: product.mrp || product.price }];
  }

  // --- Handlers ---
  const handleDecrement = () => {
    if (quantity > 1) {
      const newQty = quantity - 1;
      setQuantity(newQty);
      const isInCart = cart?.some(c => c.id === product.id && c.selectedWeight === (selectedVariant.unit || selectedVariant.size));
      if (isInCart) updateQuantity(product.id, (selectedVariant.unit || selectedVariant.size), newQty);
    }
  };

  const handleIncrement = () => {
    if (quantity < maxStock) {
      const newQty = quantity + 1;
      setQuantity(newQty);
      const isInCart = cart?.some(c => c.id === product.id && c.selectedWeight === (selectedVariant.unit || selectedVariant.size));
      if (isInCart) updateQuantity(product.id, (selectedVariant.unit || selectedVariant.size), newQty);
    }
  };

const handleAddToCart = async () => {
    const token = localStorage.getItem('user_token') || localStorage.getItem('token');

    if (!token || !user) {
      onLoginClick(); // Triggers the login popup
      return;
    }

    try {
      // Step A: Sync with Database
const response = await axios.post(`${BASE}/cart/add`, {
    product_id: product.id,
    quantity: quantity,
    // Change 'variant_unit' to 'selected_weight' to match what your Laravel SQL expects
    selected_weight: selectedVariant.unit || selectedVariant.size 
}, {
    headers: { 
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json'
    }
});

      if (response.status === 200 || response.status === 201) {
        // Step B: Update Frontend State
        const itemToCart = {
          id: product.id,
          name: product.name,
          image: product.image,
          price: selectedVariant.price,
          selectedWeight: selectedVariant.unit || selectedVariant.size,
          quantity: quantity
        };
        addToCart(itemToCart);
        // Removed alert for a smoother "Premium" feel
      }
    } catch (err) {
  console.error("Cart Error:", err.response?.data);
  // ADD THIS so the user knows it failed
  alert("Server Error: Could not add to cart. Check backend logs."); 
}
  };

  // 3. CORRECTED WISHLIST (Removed navigate('/login'))
  const handleWishlistClick = () => {
    const token = localStorage.getItem('user_token') || localStorage.getItem('token');
    
    if (!token || !user) {
      onLoginClick(); // Consistent with Dashboard
      return;
    }
    toggleWishlist(product);
  };

  const handlePincode = () => {
    if (pincode.length === 6) {
      setDeliveryMsg("Checking delivery...");
      setTimeout(() => setDeliveryMsg("✅ Delivery available within 24-48 hours"), 800);
    } else {
      setDeliveryMsg("❌ Please enter a valid 6-digit pincode");
    }
  };

  const handleReviewSubmit = (e) => {
    e.preventDefault();
    if (reviewRating === 0) return alert("Please select a rating");
    setReviewSubmitting(true);
    setTimeout(() => {
      setReviewSuccess("Thank you! Your review is being processed.");
      setReviewSubmitting(false);
      setReviewText('');
      setReviewRating(0);
    }, 1500);
  };

  const ratingCounts = [5, 4, 3, 2, 1].map(star => {
    const count = reviews.filter(r => Math.round(r.rating) === star).length;
    return { star, count, pct: reviews.length ? (count / reviews.length) * 100 : 0 };
  });

  return (
  <div className="pdv-root">
    {/* 1. CLEAN BREADCRUMBS (Removed the misplaced seller link above) */}
    <div className="pdv-breadcrumb">
      <span onClick={() => navigate('/agro')}>Home</span>
      <span className="bc-sep">›</span>
      <span>{product.category}</span>
      <span className="bc-sep">›</span>
      <span className="bc-current">{product.name}</span>
    </div>

    <div className="pdv-main">
      {/* LEFT: GALLERY */}
<div className="pdv-gallery">
  {discount > 0 && <div className="pdv-discount-badge">{discount}% OFF</div>}
  <div className="pdv-main-img-wrap">
    {/* Use the helper function here */}
    <img src={imgUrl(activeImage)} alt={product.name} className="pdv-main-img" />
  </div>
  <div className="pdv-thumbs">
    {images.map((img, i) => (
      <div 
        key={i} 
        className={`pdv-thumb ${activeImage === img ? 'active' : ''}`} 
        onClick={() => setActiveImage(img)}
      >
        <img src={imgUrl(img)} alt={`thumb-${i}`} />
      </div>
    ))}
  </div>
<button 
      className={`pdv-wishlist-btn ${isWishlisted ? 'loved' : ''}`} 
      onClick={handleWishlistClick}
    >
      {isWishlisted ? '♥ Wishlisted' : '♡ Add to Wishlist'}
    </button>
        </div>

{/* RIGHT: DETAILS */}
      <div className="pdv-details">
        <button className="pdv-back-btn" onClick={() => navigate(-1)}>← Back</button>
        
        {/* 2. CATEGORY TAG */}
        <div className="pdv-category-tag">{product.category?.toUpperCase()}</div>

        {/* 3. PRODUCT TITLE */}
        <h1 className="pdv-title" style={{ marginBottom: '4px' }}>{product.name}</h1>

       {/* 4. SELLER NAME */}
{(product.seller || product.seller_name) && (
  <div className="pdv-seller-link-container" style={{ marginBottom: '15px' }}>
    <span style={{ color: '#666', fontSize: '0.9rem' }}>Sold by: </span>
    <Link
    
             to={`/store/${product.seller_id}`} 
      className="pdv-seller-name-link"
      style={{ fontWeight: 'bold', color: '#28a745', textDecoration: 'none' }}
    >
      {/* Fallback chain: Business Name -> Company Name -> User Name -> Official Seller */}
      {product.seller?.business_name || 
       "Official Seller"}
    </Link>
  </div>
)}
         <div className="pdv-rating-summary">
            <StarRating value={avgRating} readonly />
            <span className="pdv-avg-rating">{avgRating.toFixed(1)}</span>
            <span className="pdv-review-count">({reviews.length} reviews)</span>
          </div>

          <div className="pdv-price-block">
            <span className="pdv-selling-price">₹{selectedVariant.price}</span>
            {selectedVariant.mrp > selectedVariant.price && (
              <span className="pdv-mrp">MRP ₹{selectedVariant.mrp}</span>
            )}
            <span className="pdv-tax-note">Inclusive of all taxes</span>
          </div>

          <div className="weight-selector-box" onClick={() => setShowWeightModal(true)}>
            <div className="ws-content">
              <span className="ws-label">Pack Size</span>
              <span className="ws-selected-value">
                {selectedVariant ? (selectedVariant.unit || selectedVariant.size) : 'Select Weight'}
              </span>
            </div>
            <span className="ws-arrow">▼</span>
          </div>

          {/* This is where your error was - using variantArray here */}
          <div className="pdv-weight-display-inline">
            {variantArray.map((v, i) => {
              const currentUnit = v.unit || v.size;
              const isSelected = (selectedVariant?.unit === currentUnit || selectedVariant?.size === currentUnit);
              return (
                <div 
                  key={i} 
                  className={`wm-item ${isSelected ? 'selected' : ''}`} 
                  onClick={() => { 
                    setSelectedVariant({ ...v, unit: currentUnit });
                  }}
                >
                  <div className="wm-item-info">
                    <span className="wm-unit">{currentUnit}</span>
                    <span className="wm-price">₹{v.price}</span>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="pdv-quantity-row">
            <span className="pdv-qty-label">Quantity</span>
            <div className="modern-quantity-controls">
              <button className="qty-btn minus" onClick={handleDecrement} type="button">−</button>
              <input type="number" className="qty-input" value={quantity} readOnly />
              <button className="qty-btn plus" onClick={handleIncrement} disabled={quantity >= maxStock} type="button">+</button>
            </div>
          </div>

          <div className="pdv-cta-row">
            <button className="pdv-add-cart-btn" onClick={handleAddToCart}>🛒 Add to Cart</button>
            <button className="pdv-buy-now-btn" onClick={() => { handleAddToCart(); navigate('/cart'); }}>Buy Now</button>
          </div>

          <div className="pdv-delivery-box">
            <div className="delivery-header"><span>🚚</span> Check Delivery Availability</div>
            <div className="pdv-pincode-row">
              <input type="tel" maxLength={6} placeholder="Enter pincode" value={pincode} onChange={e => setPincode(e.target.value.replace(/\D/g, ''))} />
              <button onClick={handlePincode}>Check</button>
            </div>
            {deliveryMsg && <p className="pdv-delivery-msg">{deliveryMsg}</p>}
          </div>
        </div>
      </div>

      {/* WEIGHT MODAL */}
      {showWeightModal && (
        <div className="weight-modal-overlay" onClick={() => setShowWeightModal(false)}>
          <div className="weight-modal-content" onClick={e => e.stopPropagation()}>
            <div className="wm-header">
              <h3>Choose Pack Size</h3>
              <button onClick={() => setShowWeightModal(false)}>×</button>
            </div>
            <div className="wm-list">
              {variantArray.map((v, i) => {
                const currentUnit = v.unit || v.size;
                const isSelected = selectedVariant?.unit === currentUnit || selectedVariant?.size === currentUnit;
                return (
                  <div 
                    key={i} 
                    className={`wm-item ${isSelected ? 'selected' : ''}`} 
                    onClick={() => { 
                      setSelectedVariant({ ...v, unit: currentUnit });
                      setShowWeightModal(false); 
                    }}
                  >
                    <div className="wm-item-info">
                      <span className="wm-unit">{currentUnit}</span>
                      <span className="wm-price">₹{v.price}</span>
                    </div>
                    <div className={`radio-outer ${isSelected ? 'active' : ''}`}>
                      <div className={`radio-inner ${isSelected ? 'show' : ''}`} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* TABS */}
<div className="pdv-info-section">
        <div className="pdv-tabs">
          {['overview', 'specs', 'seller'].map(tab => ( // Added 'seller' tab
            <button key={tab} className={`pdv-tab ${activeTab === tab ? 'active' : ''}`} onClick={() => setActiveTab(tab)}>
              {tab === 'specs' ? 'Specifications' : tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>
        
        <div className="pdv-tab-content">
          {activeTab === 'overview' && <p>{product.product_overview || product.description}</p>}
          
          {activeTab === 'specs' && (
            <table className="pdv-specs-table">
              <tbody>
                <tr><td>Category</td><td>{product.category}</td></tr>
                <tr><td>Stock</td><td>{maxStock} units</td></tr>
                {/* Added Seller Info to Specs */}
                <tr><td>Manufacturer</td><td>{product.seller?.company_name || 'ATR Solar'}</td></tr>
                <tr><td>License (GST/FSSAI)</td><td>{product.seller?.license_number || product.seller?.gst_number || 'N/A'}</td></tr>
              </tbody>
            </table>
          )}

          {/* NEW: DEDICATED SELLER TAB */}
          {activeTab === 'seller' && product.seller && (
            <div className="pdv-seller-detailed-info">
               <h3>{product.seller.company_name}</h3>
               <p>{product.seller.description || "Authorized partner of ATR Solar Marketplace."}</p>
               <div className="seller-contact-grid">
                  <div className="s-grid-item"><strong>GSTIN:</strong> {product.seller.gst_number}</div>
                  <div className="s-grid-item"><strong>Location:</strong> {product.seller.city || 'Madurai'}</div>
               </div>
            </div>
          )}
        </div>
      </div>

      {/* REVIEWS SECTION */}
      <div className="pdv-reviews-section">
        <h2 className="pdv-section-title">Customer Reviews</h2>
        <div className="reviews-summary">
          <div className="avg-rating-block">
            <div className="avg-big">{avgRating.toFixed(1)}</div>
            <StarRating value={avgRating} readonly />
            <div className="avg-count">{reviews.length} reviews</div>
          </div>
          <div className="rating-bars">
            {ratingCounts.map(({ star, count, pct }) => (
              <div key={star} className="rating-bar-row">
                <span className="bar-label">{star}★</span>
                <div className="rating-bar-track"><div className="rating-bar-fill" style={{ width: `${pct}%` }} /></div>
                <span className="bar-count">{count}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="reviews-list">
          {reviews.map(r => <ReviewCard key={r.id} review={r} />)}
        </div>
        <form className="review-form" onSubmit={handleReviewSubmit}>
          <h3>Write a Review</h3>
          {reviewSuccess && <div className="review-success-msg">{reviewSuccess}</div>}
          <div className="rf-group">
            <label>Rating</label>
            <StarRating value={reviewRating} onChange={setReviewRating} />
          </div>
          <div className="rf-group">
            <label>Review</label>
            <textarea rows={4} value={reviewText} onChange={e => setReviewText(e.target.value)} required />
          </div>
          <button type="submit" className="rf-submit-btn" disabled={reviewSubmitting}>Submit Review</button>
        </form>
      </div>
    </div>
  );
};

export default ProductViewPage;